import { useState } from 'react';
import { FileText, Search, Camera, Sparkles, AlertCircle, CheckCircle2, Upload, ArrowRight, X, Download } from 'lucide-react';
import ReceiptSnap from '../client-pwa/ReceiptSnap';

type FileItem = {
  id: string;
  name: string;
  category: 'Tax Return' | 'BAS' | 'P&L' | 'Letters';
  date: string;
  size: string;
  year: string;
  color: string;
};

const files: FileItem[] = [
  { id: '1', name: 'Tax Return FY24-25', category: 'Tax Return', date: '12 Apr 2026', size: '2.4 MB', year: '2026', color: 'from-violet-500 to-fuchsia-500' },
  { id: '2', name: 'P&L · March', category: 'P&L', date: '8 Apr 2026', size: '480 KB', year: '2026', color: 'from-emerald-400 to-cyan-500' },
  { id: '3', name: 'BAS Q3 2026', category: 'BAS', date: '5 Apr 2026', size: '320 KB', year: '2026', color: 'from-cyan-400 to-blue-500' },
  { id: '4', name: 'Letter of Income', category: 'Letters', date: '28 Mar 2026', size: '180 KB', year: '2026', color: 'from-amber-400 to-orange-500' },
  { id: '5', name: 'P&L · February', category: 'P&L', date: '4 Mar 2026', size: '472 KB', year: '2026', color: 'from-emerald-400 to-cyan-500' },
  { id: '6', name: 'BAS Q2 2026', category: 'BAS', date: '28 Jan 2026', size: '316 KB', year: '2026', color: 'from-cyan-400 to-blue-500' },
  { id: '7', name: 'Tax Return FY23-24', category: 'Tax Return', date: '15 Aug 2025', size: '2.1 MB', year: '2025', color: 'from-violet-500 to-fuchsia-500' },
  { id: '8', name: 'P&L · FY23-24', category: 'P&L', date: '14 Aug 2025', size: '520 KB', year: '2025', color: 'from-emerald-400 to-cyan-500' },
];

const requested = [
  { id: 'r1', title: 'Q4 bank statement', due: '30 Apr', status: 'pending' as const },
  { id: 'r2', title: 'Vehicle logbook', due: '5 May', status: 'pending' as const },
  { id: 'r3', title: 'Equipment receipts', due: '8 Apr', status: 'uploaded' as const },
];

export default function FilesPageV2() {
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<string>('All');
  const [previewFile, setPreviewFile] = useState<FileItem | null>(null);
  const [receiptOpen, setReceiptOpen] = useState(false);

  const categories = ['All', 'Tax Return', 'BAS', 'P&L', 'Letters'];
  const filtered = files.filter(f => {
    const matchSearch = f.name.toLowerCase().includes(search.toLowerCase());
    const matchCat = filter === 'All' || f.category === filter;
    return matchSearch && matchCat;
  });

  return (
    <div className="space-y-4 pt-4">
      {/* Editorial title */}
      <div className="flex items-baseline justify-between">
        <h1 className="text-3xl font-black tracking-tighter">Vault</h1>
        <p className="text-[10px] font-medium uppercase tracking-[0.25em] text-white/40">{files.length} documents</p>
      </div>

      {/* Snap receipt - premium glass card */}
      <button
        onClick={() => setReceiptOpen(true)}
        className="group relative w-full overflow-hidden rounded-3xl border border-white/10 bg-gradient-to-r from-cyan-500/20 via-violet-500/20 to-fuchsia-500/20 p-5 text-left transition-all hover:from-cyan-500/30"
      >
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(167,139,250,0.4),transparent_50%)]" />
        <div className="relative flex items-center gap-4">
          <div className="flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-cyan-400 to-violet-500 shadow-xl shadow-violet-500/40">
            <Camera className="h-6 w-6" />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <p className="text-base font-bold tracking-tight">Snap a receipt</p>
              <span className="inline-flex items-center gap-0.5 rounded-full bg-white/15 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider backdrop-blur-sm">
                <Sparkles className="h-2.5 w-2.5" />
                AI
              </span>
            </div>
            <p className="mt-0.5 text-xs text-white/60">Vendor, amount, GST extracted in 1.4s</p>
          </div>
          <ArrowRight className="h-4 w-4 text-white/40 transition-transform group-hover:translate-x-1" />
        </div>
      </button>

      {/* Requested files */}
      <div className="rounded-3xl border border-amber-300/30 bg-gradient-to-br from-amber-500/15 via-white/[0.02] to-transparent p-5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Upload className="h-4 w-4 text-amber-300" />
            <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-amber-200">Awaiting your upload</p>
          </div>
          <p className="text-[10px] text-white/40">{requested.filter(r => r.status === 'pending').length} pending</p>
        </div>
        <div className="mt-3 space-y-2">
          {requested.map(r => (
            <div key={r.id} className="flex items-center gap-3 rounded-2xl border border-white/5 bg-white/[0.03] p-3 backdrop-blur-sm">
              <div className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-xl ${
                r.status === 'uploaded' ? 'bg-emerald-500/20 text-emerald-300' : 'bg-amber-500/20 text-amber-300'
              }`}>
                {r.status === 'uploaded' ? <CheckCircle2 className="h-4 w-4" /> : <AlertCircle className="h-4 w-4" />}
              </div>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-semibold">{r.title}</p>
                <p className={`text-[10px] uppercase tracking-wider ${
                  r.status === 'uploaded' ? 'text-emerald-300' : 'text-amber-300'
                }`}>
                  {r.status === 'uploaded' ? `Uploaded · ${r.due}` : `Due · ${r.due}`}
                </p>
              </div>
              {r.status === 'pending' && (
                <button className="flex-shrink-0 rounded-xl bg-white text-[11px] font-bold text-[#2a2a3a] px-3 py-1.5 hover:bg-white/90">
                  Upload
                </button>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-white/40" />
        <input
          type="text"
          placeholder="Search vault…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="w-full rounded-2xl border border-white/10 bg-white/5 py-3 pl-11 pr-4 text-sm placeholder:text-white/40 backdrop-blur-md focus:border-violet-400 focus:bg-white/10 focus:outline-none"
        />
      </div>

      {/* Category pills */}
      <div className="-mx-1 flex gap-2 overflow-x-auto px-1 pb-1">
        {categories.map(c => (
          <button
            key={c}
            onClick={() => setFilter(c)}
            className={`flex-shrink-0 rounded-full border px-3.5 py-1.5 text-xs font-semibold transition-all ${
              filter === c
                ? 'border-violet-400 bg-gradient-to-r from-violet-500 to-fuchsia-500 text-white shadow-lg shadow-violet-500/30'
                : 'border-white/10 bg-white/5 text-white/60 hover:bg-white/10'
            }`}
          >
            {c}
          </button>
        ))}
      </div>

      {/* File grid - masonry-ish cards */}
      <div className="grid grid-cols-2 gap-3">
        {filtered.map(file => (
          <button
            key={file.id}
            onClick={() => setPreviewFile(file)}
            className="group relative aspect-[3/4] overflow-hidden rounded-3xl border border-white/10 bg-white/[0.03] p-4 text-left backdrop-blur-md transition-all hover:scale-[1.02] hover:border-white/20"
          >
            <div className={`absolute inset-x-0 top-0 h-32 bg-gradient-to-br ${file.color} opacity-30`} />
            <div className="absolute inset-0 bg-gradient-to-t from-[#2a2a3a] via-[#2a2a3a]/60 to-transparent" />
            <div className="relative flex h-full flex-col">
              <FileText className="h-6 w-6 text-white/80" />
              <div className="flex-1" />
              <p className="text-[9px] font-bold uppercase tracking-wider text-white/50">{file.category}</p>
              <p className="mt-1 text-sm font-bold leading-tight">{file.name}</p>
              <div className="mt-1.5 flex items-center justify-between text-[10px] text-white/40">
                <span>{file.date.split(' ').slice(0, 2).join(' ')}</span>
                <span>{file.size}</span>
              </div>
            </div>
          </button>
        ))}
      </div>

      <ReceiptSnap open={receiptOpen} onClose={() => setReceiptOpen(false)} />

      {previewFile && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-md">
          <div className="absolute inset-x-0 bottom-0 top-12 mx-auto w-full max-w-[640px] overflow-hidden rounded-t-[2rem] border border-white/10 bg-[#2a2a3a]">
            <div className="flex items-center justify-between border-b border-white/10 px-5 py-4">
              <div className="min-w-0 flex-1">
                <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-white/50">{previewFile.category}</p>
                <p className="truncate text-base font-bold">{previewFile.name}</p>
              </div>
              <button onClick={() => setPreviewFile(null)} className="flex h-9 w-9 items-center justify-center rounded-full border border-white/10 bg-white/5 text-white/70 hover:bg-white/10">
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="flex h-[calc(100%-160px)] items-center justify-center bg-gradient-to-br from-violet-900/20 to-transparent">
              <div className="text-center">
                <FileText className="mx-auto h-20 w-20 text-white/20" />
                <p className="mt-4 text-sm text-white/60">PDF preview area</p>
              </div>
            </div>
            <div className="border-t border-white/10 p-4">
              <button className="flex w-full items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-violet-500 to-fuchsia-500 py-3.5 text-sm font-bold text-white shadow-lg shadow-violet-500/30">
                <Download className="h-4 w-4" /> Download PDF
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
